import type { ComponentProps, ReactElement, ReactNode, Ref } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { CircularProgress } from "@privacy/ui";
import TextField from "@mui/material/TextField";
import { useTranslation } from "@privacy/translations";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { TenantIcon3 } from "./icons";
import { AutocompleteOption } from "./Autocomplete";

type TenantHierarchy<Data = unknown> = {
  id: string;
  organizationName: string;
  parentId?: string | null;
  children: TenantHierarchy<Data>[];
} & Data;

type TenantSelectProps<Data> = {
  tenantHierarchy: TenantHierarchy<Data> | undefined;
  selectedId: TenantHierarchy<Data>["id"] | null;
  onSelect: (selectedTenant: TenantHierarchy<Data>) => void;
  renderOption: (optionElement: ReactElement, tenantHierarchy: TenantHierarchy<Data>) => ReactNode;
  onBlur?: () => void;
  onFocus?: () => void;
  onMouseOver?: () => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  inputRef?: Ref<HTMLInputElement>;
  error?: boolean;

  forceDisabledTenant?: (tenantHierarchy: TenantHierarchy<Data>) => boolean;
  /** This is used to show the selected name while loading tenantHierarchy. */
  selectedNameWhileLoading?: string;
  InputProps?: NonNullable<ComponentProps<typeof TextField>["slotProps"]>["input"];

  blurOnSelect?: boolean;
};

type FlattenedNode<Data = unknown> = TenantHierarchy<Data> & {
  ancestors: TenantHierarchy<Data>[];
  level: number;
  disabled?: boolean;
};

export const TenantSelect = <Data,>({
  tenantHierarchy,
  selectedId,
  onSelect,
  inputRef,
  error,
  forceDisabledTenant,
  InputProps,
  selectedNameWhileLoading,
  blurOnSelect,
  placeholder,
  renderOption,
  ...props
}: TenantSelectProps<Data>) => {
  const { t } = useTranslation();
  /**
   * HACK: while loading we seed a single empty placeholder option instead of `[]`, so the dropdown
   * has a row to render the loading spinner on (see `TenantOption`); with `[]` MUI shows "No options"
   * instead. The placeholder has no `organizationName`, hence the optional-chaining guard (`?.`) in `filter()`.
   */
  let flattenedNodes = tenantHierarchy ? flattenTenantHierarchy(tenantHierarchy) : [{} as FlattenedNode<Data>];

  let selected = flattenedNodes.find(flattenedNode => flattenedNode.id === selectedId) ?? null;

  if (!tenantHierarchy && selectedNameWhileLoading) {
    const selectedWhileLoading = { organizationName: selectedNameWhileLoading } as FlattenedNode<Data>;
    flattenedNodes = [selectedWhileLoading];
    selected = selectedWhileLoading;
  }

  return (
    <Autocomplete<FlattenedNode<Data>>
      {...props}
      noOptionsText={t("TenantSelect.NoOptions")}
      blurOnSelect={blurOnSelect}
      // @ts-expect-error - not manage to type this correctly, but it works TODO: fix this type error when Andreas is back.
      disableClearable={Boolean(selected)}
      options={flattenedNodes}
      getOptionLabel={option => option.organizationName}
      getOptionDisabled={option => Boolean(option.disabled || forceDisabledTenant?.(option))}
      renderOption={({ key, ...optionsProps }, option) =>
        renderOption(
          <Box key={key}>
            <TenantOption option={option} optionsProps={optionsProps} tenantHierarchy={tenantHierarchy} />
          </Box>,
          option
        )
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={selected}
      onChange={(_, value) => typeof value !== "string" && value && onSelect(value)}
      filterOptions={(options, state) => filter(options, state.inputValue)}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          inputRef={inputRef}
          error={error}
          placeholder={selectedNameWhileLoading ? undefined : placeholder || t("TenantSelect.Placeholder")}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              startAdornment: selectedId && <TenantIcon3 size={16} />,
              ...InputProps,
            },
          }}
        />
      )}
    />
  );
};

export const flattenTenantHierarchy = <Data,>(
  tenant: TenantHierarchy<Data>,
  ancestors: TenantHierarchy<Data>[] = [],
  level: number = 0
): FlattenedNode<Data>[] => {
  return [
    { ...tenant, ancestors, level },
    ...tenant.children.flatMap(child => flattenTenantHierarchy(child, [...ancestors, tenant], level + 1)),
  ];
};

export const filter = <Data,>(flattened: FlattenedNode<Data>[], query: string): FlattenedNode<Data>[] => {
  if (query === "") return flattened;

  const matches = flattened.filter(flattenedNode =>
    flattenedNode.organizationName?.toLowerCase().includes(query.toLowerCase())
  );

  const visibleTenants = Object.fromEntries(matches.map(tenant => [tenant.id, tenant] as const));

  matches.forEach(match => {
    match.ancestors.forEach(ancestor => {
      if (ancestor.id in visibleTenants) return;

      const flattenedAncestor = flattened.find(flattenedNode => flattenedNode.id === ancestor.id);
      if (flattenedAncestor) {
        visibleTenants[flattenedAncestor.id] = { ...flattenedAncestor, disabled: true };
      }
    });
  });

  return flattened
    .filter(tenant => tenant.id in visibleTenants)
    .map(tenant => ({
      ...tenant,
      disabled: Boolean(visibleTenants[tenant.id].disabled),
    }));
};

export const TenantOption = ({
  option,
  optionsProps,
  tenantHierarchy,
  endAdornment,
}: {
  option: FlattenedNode;
  optionsProps: React.HTMLAttributes<HTMLLIElement>;
  tenantHierarchy: TenantHierarchy | undefined;
  endAdornment?: ReactNode;
}) => {
  return !tenantHierarchy ? (
    <Stack sx={{ justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
      <CircularProgress size={16} />
    </Stack>
  ) : (
    <AutocompleteOption
      sx={{ pl: theme => `${theme.spacing(option.level * 2 + 2)} !important` }}
      dense
      option={{ label: option.organizationName, value: option.id }}
      endAdornment={endAdornment}
      {...optionsProps}
    />
  );
};
